import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CreateDocumentTypeRequest } from '../redux/DocumentRedux';
import { LabeledTextField } from '@/__core/components/LabeledField';
import axiosInstance from '@/setup/axios/axiosInstance';

interface DocumentTypeManagerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (documentType: any) => void;
}

interface DocumentTypeForm {
  name: string;
  description: string;
}

const DocumentTypeManager: React.FC<DocumentTypeManagerProps> = ({ 
  open, 
  onClose, 
  onSuccess 
}) => {
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState<DocumentTypeForm>({
    name: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData: CreateDocumentTypeRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      };

      const response = await axiosInstance.post('/documents/document-types/', requestData);
      
      // Reset form and close dialog
      setFormData({
        name: '',
        description: ''
      });
      
      // Pass the created document type to the success callback
      onSuccess?.(response.data);
      onClose();
    } catch (error: any) {
      
      // Handle different types of errors
      let errorMessage = 'Có lỗi xảy ra khi tạo loại tài liệu';
      
      if (error.response?.data?.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          errorMessage = errors[0].detail || errorMessage;
        }
      } else if (error.response?.data?.detail) {
        // Handle general API errors
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        // Handle network errors
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: ''
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>Tạo loại tài liệu mới</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <LabeledTextField
            label="Tên loại tài liệu"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            placeholder="Ví dụ: Bản vẽ kỹ thuật"
            disabled={loading}
          />
          
          <LabeledTextField
            label="Mô tả"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            placeholder="Mô tả chi tiết về loại tài liệu này"
            disabled={loading}
          />
          
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Đang tạo...' : 'Tạo loại tài liệu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentTypeManager;
